import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { useAuth } from '../auth/AuthContext.jsx';
import './LoginPage.css';

export function LoginPage() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    if (user) {
        const dest = location.state?.from?.pathname || '/pipeline';
        return <Navigate to={dest} replace />;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            const loggedInUser = await login(email, password);
            if (loggedInUser?.force_password_change === 1) {
                navigate('/change-password', { replace: true });
            } else {
                const dest = location.state?.from?.pathname || '/pipeline';
                navigate(dest, { replace: true });
            }
        } catch (err) {
            setError(err.message || 'Sign in failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="login">
            {/* LEFT: editorial brand panel */}
            <section className="login-brand">
                <div className="login-brand-inner">
                    <div className="login-mark">
                        <img src="/behold-logo-white.png" alt="" className="login-mark-glyph" />
                        <span className="login-mark-text display">Behold International</span>
                    </div>

                    <div className="login-quote">
                        <h1 className="display login-headline">Empowering <span className="accent">sustainable entrepreneurship</span> beyond borders</h1>
                        <p className="login-tagline">
                            The pipeline behind every deal. From first lead to closed round, organized and traceable across the portfolio.
                        </p>
                    </div>

                    <div className="login-meta">
                        <span className="mono">Behold International</span>
                        <span className="mono">Investment Pipeline</span>
                    </div>
                </div>
            </section>

            {/* RIGHT: form */}
            <section className="login-form-panel">
                <form className="login-form" onSubmit={handleSubmit}>
                    <div className="login-form-header">
                        <h2 className="display">Welcome back</h2>
                        <p>Sign in to continue.</p>
                    </div>

                    <label className="field">
                        <span className="field-label">Email</span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                            autoFocus
                            placeholder="you@behold.nl"
                        />
                    </label>

                    <label className="field">
                        <span className="field-label">Password</span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                            placeholder="••••••••••"
                        />
                    </label>

                    {error && <div className="field-error">{error}</div>}

                    <button type="submit" className="btn-primary" disabled={submitting}>
                        {submitting ? <span className="spinner" /> : (
                            <>
                                Sign in
                                <ArrowRight size={16} strokeWidth={2} />
                            </>
                        )}
                    </button>

                    <p className="login-help">
                        Lost access? Contact your admin to reset your password.
                    </p>
                </form>
            </section>
        </div>
    );
}
